import React, { useCallback, useMemo } from 'react';
import c from 'classnames';

import getOS, { OS } from '@lyearn/core/utils/getOS';
import { useTranslation } from '@lyearn/i18n';
import { MoreVertical, VideoPlaceholder } from '@lyearn/icons';
import { useReactNodeView } from '@lyearn/lygo-core';
import VideoPlayer from '@lyearn/shared/components/Players';
import { Input, Stack, Typography, useSnackbar } from '@lyearn/ui';

import RedirectLinkEditor from '../../../components/RedirectLinkEditor';
import { allTransition, nodeWidth, ResizeContainer } from '../../../components/ResizeContainer';
import { UploadButton } from '../../../components/UploadButton';
import { UploadCard } from '../../../components/UploadCard';
import { usePortalForForms } from '../../../hooks/usePortalForForms';
import { VideoComponentProps } from '../types';
import { getTracks, replaceURLHost } from './utils';
import VideoIcon from './videoSvg';
import VideoTitleAndDescription from './videoTitleAndDescription';
const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
  event.stopPropagation();
};

const renderFullWidthVideo = (
  props: VideoComponentProps,
  plyrTracks: {
    label: string;
    srcLang: string;
    src: string;
  }[],
  showSnackbarError: () => void,
) => {
  const {
    src,
    width,
    initialProgress,
    maxPlayerProgress,
    onLoad,
    onProgress,
    allowForwarding,
    sections,
  } = props;
  return (
    <div className={c(allTransition, 'my-8 w-full')}>
      <VideoPlayer
        key={sections?.length ? JSON.stringify(sections) : undefined}
        initialProgress={initialProgress}
        loaderWidth={width}
        maxPlayerProgress={maxPlayerProgress}
        provider="html5"
        sections={sections}
        tracks={plyrTracks}
        url={src!}
        onLoad={onLoad}
        onProgress={onProgress}
        onSeek={!allowForwarding ? showSnackbarError : undefined}
      />
    </div>
  );
};

const VideoComponent: React.FC<VideoComponentProps> = (props) => {
  const {
    readOnly,
    isUploading,
    progress,
    progressFileName,
    id,
    width,
    caption,
    onProgress,
    onLoad,
    isProcessing,
    allowForwarding,
    allowBackgroundVideoPlayback,
    allowPlaybackSpeedControl,
    maxPlayerProgress,
    initialProgress,
    captions,
    disabedCaptions,
    showFullWidth,
    processingProgress,
    sections,
    overlay,
    allowDownloading,
    onDownload,
    downloadableUrl,
    enableDownloadVideo,
  } = props;
  const videoRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { showErrorSnackbar } = useSnackbar();
  const { node, isMicroLearning = false } = useReactNodeView();
  const videoAttributeRef = React.useRef<HTMLDivElement>(null);

  const videoAttributesFrom = usePortalForForms(
    videoAttributeRef,
    <Stack className="" direction="col" gapY={10}>
      <VideoTitleAndDescription />
    </Stack>,
    !isMicroLearning,
  );
  // ios devices does not support dash(mpd) format so using hls(m3u8)
  let src = getOS() === OS.iOS ? props.src?.replace('.mpd', '.m3u8') : props.src;
  // todo remove after backend migration
  src = src && replaceURLHost(src);
  const showSnackbarError = useCallback(() => {
    showErrorSnackbar({
      message: t('Lygo:AdvancedMenu.video.AllowForwarding.error'),
      position: { horizontal: 'right', vertical: 'bottom' },
    });
  }, [showErrorSnackbar, t]);

  const plyrTracks = useMemo(
    () => getTracks(captions || {}, disabedCaptions || [], t),
    [captions, disabedCaptions, t],
  );

  return (
    <>
      {src && !isProcessing ? (
        showFullWidth ? (
          renderFullWidthVideo(props, plyrTracks, showSnackbarError)
        ) : isMicroLearning ? (
          <Stack
            className={c(
              // nodeWidth(calculatedWidth),
              allTransition,
              'bg-bg-hover-light w-full !max-w-full mt-24  rounded-16 border border-border',
            )}
            direction="col"
            onClick={onMouseDown}
            onMouseDown={onMouseDown}>
            <Stack
              alignItems="center"
              className="h-[6.2rem] p-12 border-b border-border"
              justifyContent="between">
              <Stack alignItems="center" className="" gapX={12}>
                <VideoIcon />
                <Stack direction="col">
                  <Typography numberOfLines={1} variant="body-short-02">
                    {node?.attrs.name ?? 'Unnamed.mov'}
                  </Typography>
                  <Typography className="text-secondary" variant="body-short-03">
                    Preview
                  </Typography>
                </Stack>
              </Stack>
              {/* <MoreVertical className="h-24 w-24" fillColorToken="icon-secondary" /> */}
            </Stack>
            <ResizeContainer
              caption={caption}
              isMicroLearning={true}
              maxHeight={400}
              nodeRef={videoRef}
              readOnly={true}
              width={width as number}>
              {({ calculatedWidth }: { calculatedWidth: number }) => (
                <Stack ref={videoRef} className=" px-24 " direction="col" gapY={24}>
                  <VideoPlayer
                    key={sections?.length ? JSON.stringify(sections) : undefined}
                    allowBackgroundVideoPlayback={allowBackgroundVideoPlayback}
                    allowDownloading={allowDownloading}
                    allowForwarding={allowForwarding}
                    allowPlaybackSpeedControl={allowPlaybackSpeedControl}
                    downloadableUrl={downloadableUrl}
                    enableDownloadVideo={enableDownloadVideo}
                    id={id}
                    initialProgress={initialProgress}
                    loaderWidth={calculatedWidth}
                    maxPlayerProgress={maxPlayerProgress}
                    overlay={overlay}
                    provider="html5"
                    sections={sections}
                    tracks={plyrTracks}
                    url={src!}
                    onDownload={onDownload}
                    onLoad={onLoad}
                    onProgress={onProgress}
                    onSeek={!allowForwarding ? showSnackbarError : undefined}
                  />
                </Stack>
              )}
            </ResizeContainer>
            <Stack className="w-full p-24" direction="col" gapY={16}>
              <Stack ref={videoAttributeRef} className="h-[11rem] w-full" direction="col">
                {videoAttributesFrom}
              </Stack>
              <RedirectLinkEditor />
            </Stack>
          </Stack>
        ) : (
          <ResizeContainer
            caption={caption}
            nodeRef={videoRef}
            readOnly={readOnly}
            width={width as number}>
            {({ calculatedWidth }: { calculatedWidth: number }) => (
              <div
                ref={videoRef}
                className={c(nodeWidth(calculatedWidth), allTransition)}
                onClick={onMouseDown}
                onMouseDown={onMouseDown}>
                <VideoPlayer
                  key={sections?.length ? JSON.stringify(sections) : undefined}
                  allowBackgroundVideoPlayback={allowBackgroundVideoPlayback}
                  allowDownloading={allowDownloading}
                  allowForwarding={allowForwarding}
                  allowPlaybackSpeedControl={allowPlaybackSpeedControl}
                  downloadableUrl={downloadableUrl}
                  enableDownloadVideo={enableDownloadVideo}
                  id={id}
                  initialProgress={initialProgress}
                  loaderWidth={calculatedWidth}
                  maxPlayerProgress={maxPlayerProgress}
                  overlay={overlay}
                  provider="html5"
                  sections={sections}
                  tracks={plyrTracks}
                  url={src!}
                  onDownload={onDownload}
                  onLoad={onLoad}
                  onProgress={onProgress}
                  onSeek={!allowForwarding ? showSnackbarError : undefined}
                />
              </div>
            )}
          </ResizeContainer>
        )
      ) : !src && readOnly ? (
        <div />
      ) : isUploading || isProcessing ? (
        <div className="mt-16">
          <UploadCard
            PlaceholderIcon={VideoPlaceholder}
            isProcessing={isProcessing}
            isUploading={isUploading}
            name={progressFileName}
            processingProgress={processingProgress}
            progress={progress}
          />
        </div>
      ) : (
        <UploadButton
          PlaceholderIcon={VideoPlaceholder}
          id={id as string}
          placeHolder={t('Lygo:UploadVideo')}
          type={'video/*'}
        />
      )}
    </>
  );
};

export default VideoComponent;
